import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, Clock, Star, Bug, Plus, Wrench, AlertCircle } from 'lucide-react';
import { currentVersion, versionHistory, getVersionBadgeColor, VersionInfo } from '@/config/version-info';
import { cn } from '@/lib/utils';

const AdminVersionInfo = () => {
  const [openVersions, setOpenVersions] = useState<string[]>([currentVersion.version]);

  const toggleVersion = (version: string) => {
    setOpenVersions(prev => 
      prev.includes(version) 
        ? prev.filter(v => v !== version)
        : [...prev, version]
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderChangelog = (changelog: VersionInfo['changelog']) => {
    return (
      <div className="space-y-4 mt-4">
        {changelog.new && changelog.new.length > 0 && (
          <div>
            <div className="flex items-center mb-2">
              <Plus className="h-4 w-4 text-green-600 mr-2" />
              <h4 className="font-medium text-green-800">New Features</h4>
            </div>
            <ul className="space-y-1 ml-6">
              {changelog.new.map((item, index) => (
                <li key={index} className="text-sm text-gray-700 flex items-start">
                  <span className="w-1 h-1 bg-green-600 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {changelog.improved && changelog.improved.length > 0 && (
          <div>
            <div className="flex items-center mb-2">
              <Wrench className="h-4 w-4 text-blue-600 mr-2" />
              <h4 className="font-medium text-blue-800">Improvements</h4>
            </div>
            <ul className="space-y-1 ml-6">
              {changelog.improved.map((item, index) => (
                <li key={index} className="text-sm text-gray-700 flex items-start">
                  <span className="w-1 h-1 bg-blue-600 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {changelog.fixed && changelog.fixed.length > 0 && (
          <div>
            <div className="flex items-center mb-2">
              <Bug className="h-4 w-4 text-orange-600 mr-2" />
              <h4 className="font-medium text-orange-800">Bug Fixes</h4>
            </div>
            <ul className="space-y-1 ml-6">
              {changelog.fixed.map((item, index) => (
                <li key={index} className="text-sm text-gray-700 flex items-start">
                  <span className="w-1 h-1 bg-orange-600 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Version Information</h1>
        <p className="text-gray-600">Track platform updates, new features, and improvements</p>
      </div>

      {/* Current Version Highlight */}
      <Card className="mb-8 border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-3">
                <Star className="h-6 w-6 text-primary" />
                Current Version {currentVersion.version}
              </CardTitle>
              <CardDescription className="flex items-center gap-2 mt-2">
                <Clock className="h-4 w-4" />
                Released on {formatDate(currentVersion.releaseDate)}
              </CardDescription>
            </div>
            <Badge className={cn("text-sm font-medium border", getVersionBadgeColor(currentVersion.releaseType))}>
              {currentVersion.releaseType.toUpperCase()} RELEASE
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {renderChangelog(currentVersion.changelog)}
        </CardContent>
      </Card>

      {/* Version History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Version History
          </CardTitle>
          <CardDescription>
            Complete changelog of all platform updates
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {versionHistory.slice(1).map((version) => (
            <Collapsible 
              key={version.version}
              open={openVersions.includes(version.version)}
              onOpenChange={() => toggleVersion(version.version)}
            >
              <CollapsibleTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="w-full justify-between p-4 h-auto border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-left">
                      <div className="font-semibold text-base">Version {version.version}</div>
                      <div className="text-sm text-gray-500">
                        {formatDate(version.releaseDate)}
                      </div>
                    </div>
                    <Badge className={cn("text-xs border", getVersionBadgeColor(version.releaseType))}>
                      {version.releaseType}
                    </Badge>
                  </div>
                  <ChevronDown 
                    className={cn(
                      "h-4 w-4 transition-transform duration-200",
                      openVersions.includes(version.version) ? "rotate-180" : ""
                    )} 
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="px-4 pb-4">
                {renderChangelog(version.changelog)}
              </CollapsibleContent>
            </Collapsible>
          ))}
        </CardContent>
      </Card>

      {/* Update Information */}
      <Card className="mt-8 bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-900">Stay Updated</CardTitle>
          <CardDescription className="text-blue-700">
            This version information is updated with each platform deployment. 
            Check back regularly to see the latest features and improvements.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
};

export default AdminVersionInfo;